/**
 * The builder's doors onto the vocabulary — Stories 27.2 and 27.5, RULE-019.
 *
 * PRD §3.5: the AI emits "a fixed tool vocabulary (`add_step`, `add_question`, `set_option`,
 * `set_criterion`, `add_branch`, `set_band`, `set_outcome`, `set_persona`, `add_action`,
 * `add_extraction_field`, `set_icp_profile`) which are the *same* operations the human builder
 * UI calls." So when a person adds a step, a question, an option, a criterion, a rule, a band,
 * an outcome, an action or an extraction field, or describes the ideal customer, the builder
 * calls the function a model's proposal calls — `applyOperation` in `packages/schema` — and not
 * a recipe of its own. One implementation of each edit, not two that can drift.
 *
 * EACH IS A RECIPE FOR `edit()`, and the operation is what it runs: the change is a patch on
 * the one stack (RULE-018), and an operation that refuses — an id naming nothing, an id taken —
 * changes nothing, so `edit()` records no step for it. The whole funnel is still judged at
 * save, as it always was: a band boundary mid-drag and a weight typed one digit at a time pass
 * through states the save refuses, and the operations let them.
 *
 * The eleven, and the door each has:
 *
 *   add_step              the step list's "+ Add step" (`insertStep`), and the palette's first step
 *   add_question          the palette (`addBlockTo`)
 *   set_option            the inspector's options — add, rename, duplicate (here), and their
 *                         points and Disqualify switch (`setOptionPoints`, `setDisqualifying`,
 *                         rubric.ts)
 *   set_criterion         "Add a criterion" (`scoreQuestion` here; `addExtractedCriterion`,
 *                         `addFitCriterion`), a weight (`setWeight`), an extracted reading's
 *                         points (`extracted-points.ts`)
 *   add_branch            the Logic tab's chooser and Duplicate (`insertRule`, `duplicateRule`)
 *   set_band              a band's name, bounds and Qualified switch (`setBandFields`)
 *   set_outcome           no control yet — nothing in the builder edits an outcome
 *   set_persona           no control until the Voice tab (M5)
 *   add_action            "Add an action" on a band's routing card (`addActionTo`)
 *   add_extraction_field  the extraction card's "Add a field" (`addExtractionField`, extraction.ts)
 *   set_icp_profile       "Describe your ideal customer" (`setIcpProfile`, escalation.ts)
 *
 * `tests/editor-vocabulary-door.test.ts` holds the door: an editor module that adds any of
 * these by pushing into the funnel, or writes the description itself, goes red.
 */
import {
  applyOperation,
  type ActionKind,
  type BlockType,
  type Criterion,
  type Funnel,
  type LogicRule,
} from '@leadgible/schema'

/**
 * A block of a type, into the step the selected block is on — or the last step, or a first
 * step made for it when there is none. A block that belongs to no step never renders and never
 * scores, so there is always one to put it in.
 */
export function addBlockTo(
  draft: Funnel,
  /** `statement` is a consent block's wording, when the organisation has set a default for it (Story 26.6). */
  block: { type: BlockType; id: string; statement?: string },
  firstStepId: string,
  selected: string | null,
): void {
  if (draft.steps.length === 0) applyOperation(draft, { op: 'add_step', id: firstStepId })
  const target =
    (selected === null ? undefined : draft.steps.find((step) => step.blocks.includes(selected))) ??
    draft.steps[draft.steps.length - 1]
  if (target === undefined) return
  applyOperation(draft, { op: 'add_question', step_id: target.id, block })
}

/** A step, at a position — named by where it lands, as the operation names it. */
export function insertStep(draft: Funnel, id: string, position: number): void {
  applyOperation(draft, { op: 'add_step', id, position })
}

/** A new option, called what the inspector calls one before it is named. */
export function addOptionTo(draft: Funnel, blockId: string, id: string): void {
  applyOperation(draft, { op: 'set_option', block_id: blockId, id, label: 'New option' })
}

export function renameOption(draft: Funnel, blockId: string, optionId: string, label: string): void {
  applyOperation(draft, { op: 'set_option', block_id: blockId, id: optionId, label })
}

/** A copy of an option beside it: a new id, the same words and "copy". */
export function duplicateOption(draft: Funnel, blockId: string, id: string, label: string): void {
  applyOperation(draft, { op: 'set_option', block_id: blockId, id, label: `${label} copy` })
}

/** The Qualify tab's "Add a criterion" for a question: its words, the default weight, points to come. */
export function scoreQuestion(draft: Funnel, blockId: string): void {
  applyOperation(draft, { op: 'set_criterion', source: 'answer_ref', block_id: blockId })
}

/**
 * What a criterion reads, as `set_criterion` names it. `transcript` is M5 and no operation
 * writes one, so its criterion has no door and a change to it is refused.
 */
export function readingOf(
  criterion: Criterion,
):
  | { source: 'answer_ref'; block_id: string }
  | { source: 'extracted'; field_key: string }
  | { source: 'derived' | 'metadata'; key: Extract<Criterion, { source: 'derived' | 'metadata' }>['key'] }
  | null {
  switch (criterion.source) {
    case 'answer_ref':
      return { source: 'answer_ref', block_id: criterion.block_id }
    case 'extracted':
      return { source: 'extracted', field_key: criterion.field_key }
    case 'derived':
      return { source: 'derived', key: criterion.key }
    case 'metadata':
      return { source: 'metadata', key: criterion.key }
    case 'transcript':
      return null
  }
}

/** A criterion's weight, written as typed — the Qualify tab refuses a blank or a zero before it gets here. */
export function setWeight(draft: Funnel, criterionId: string, weight: number): void {
  const criterion = draft.rubric.criteria.find((candidate) => candidate.id === criterionId)
  const reading = criterion === undefined ? null : readingOf(criterion)
  if (criterion === undefined || reading === null) return
  applyOperation(draft, { op: 'set_criterion', id: criterion.id, ...reading, weight })
}

/** A band's name, bounds or Qualified switch — whichever the Qualify tab changed. */
export function setBandFields(
  draft: Funnel,
  bandId: string,
  fields: { label?: string; min?: number; max?: number; is_qualified?: boolean },
): void {
  applyOperation(draft, { op: 'set_band', id: bandId, ...fields })
}

/** A rule, at the end unless a position is given. */
export function insertRule(draft: Funnel, rule: LogicRule, position?: number): void {
  applyOperation(draft, {
    op: 'add_branch',
    id: rule.id,
    ...(rule.step_id !== undefined ? { step_id: rule.step_id } : {}),
    when: rule.when,
    then: rule.then,
    ...(position !== undefined ? { position } : {}),
  })
}

/** A copy of a rule, straight after it, under a new id. */
export function duplicateRule(draft: Funnel, ruleId: string, id: string): void {
  const at = draft.logic.findIndex((rule) => rule.id === ruleId)
  const rule = draft.logic[at]
  if (rule === undefined) return
  insertRule(draft, { ...rule, id }, at + 1)
}

/** A new action on a band — instant, and to nobody until the operator says (RULE-067). */
export function addActionTo(draft: Funnel, bandId: string, id: string, kind: ActionKind): void {
  applyOperation(draft, { op: 'add_action', id, band_id: bandId, kind })
}
